import type { EventPage, PublicSnapshot, PublicVesselDetail } from "../../shared/contracts";
import type { ZodType } from "zod";
import { eventPageSchema, publicSnapshotSchema, publicVesselDetailSchema } from "./schema";

export const PUBLIC_API_TIMEOUT_MS = 12_000;

export class PublicApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "PublicApiError";
  }
}

async function getJson<T>(url: string, schema: ZodType<T>, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  const cancel = () => controller.abort(signal?.reason);
  if (signal?.aborted) cancel();
  else signal?.addEventListener("abort", cancel, { once: true });
  const timeout = setTimeout(() => controller.abort(new DOMException("The live service timed out", "TimeoutError")), PUBLIC_API_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) throw new PublicApiError("The live service did not respond", response.status);
    const result = schema.safeParse(await response.json());
    controller.signal.throwIfAborted();
    if (!result.success) throw new PublicApiError("The live service returned invalid data", 502);
    return result.data;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", cancel);
  }
}

export function fetchSnapshot(signal?: AbortSignal): Promise<PublicSnapshot> {
  return getJson("/api/v1/snapshot?contract=9", publicSnapshotSchema, signal);
}

export function fetchEventPage(cursor: string | null, signal?: AbortSignal): Promise<EventPage> {
  const query = new URLSearchParams({ limit: "25" });
  if (cursor) query.set("cursor", cursor);
  return getJson(`/api/v1/events?${query.toString()}`, eventPageSchema, signal);
}

export function fetchVesselDetail(id: string, signal?: AbortSignal): Promise<PublicVesselDetail> {
  return getJson(`/api/v1/vessels/${encodeURIComponent(id)}?contract=9`, publicVesselDetailSchema, signal);
}
